import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
// NOTE: This file uses @tabler/icons-react exclusively for OS/brand icons
// These brand-specific icons are not available in lucide-react
import {
  IconBrandApple,
  IconBrandDebian,
  IconBrandDocker,
  IconBrandRedhat,
  IconBrandUbuntu,
  IconBrandWindows,
  IconServer,
} from "@tabler/icons-react";

type OSIconProps = {
  os: string;
  size?: number;
  className?: string;
};

export function OSIcon({ os, size = 16, className }: OSIconProps) {
  const osLower = os.toLowerCase();

  const iconProps = {
    size,
    className: cn("text-muted-foreground cursor-help", className),
    stroke: 1.5,
  };

  let icon: React.ReactNode;
  let tooltipText: string;

  if (osLower.includes("windows") || osLower.includes("win")) {
    icon = <IconBrandWindows {...iconProps} />;
    tooltipText = "Windows";
  } else if (osLower.includes("ubuntu")) {
    icon = <IconBrandUbuntu {...iconProps} />;
    tooltipText = "Ubuntu";
  } else if (osLower.includes("debian")) {
    icon = <IconBrandDebian {...iconProps} />;
    tooltipText = "Debian";
  } else if (
    osLower.includes("rpm") ||
    osLower.includes("redhat") ||
    osLower.includes("rhel") ||
    osLower.includes("centos") ||
    osLower.includes("fedora")
  ) {
    icon = <IconBrandRedhat {...iconProps} />;
    if (osLower.includes("centos")) {
      tooltipText = "CentOS";
    } else if (osLower.includes("fedora")) {
      tooltipText = "Fedora";
    } else {
      tooltipText = "Red Hat Enterprise Linux";
    }
  } else if (
    osLower.includes("darwin") ||
    osLower.includes("macos") ||
    osLower.includes("osx") ||
    osLower.includes("mac")
  ) {
    icon = <IconBrandApple {...iconProps} />;
    tooltipText = "macOS";
  } else if (osLower.includes("docker") || osLower.includes("container")) {
    icon = <IconBrandDocker {...iconProps} />;
    tooltipText = "Container/Docker";
  } else if (osLower.includes("linux")) {
    icon = <IconServer {...iconProps} />;
    tooltipText = "Linux";
  } else {
    icon = <IconServer {...iconProps} />;
    tooltipText = os;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center">{icon}</span>
      </TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  );
}

// Helper function to normalize OS to icon type
export function getOSIconType(os: string): string {
  const osLower = os.toLowerCase();

  if (osLower.includes("windows") || osLower.includes("win")) {
    return "windows";
  } else if (osLower.includes("ubuntu")) {
    return "ubuntu";
  } else if (osLower.includes("debian")) {
    return "debian";
  } else if (
    osLower.includes("rpm") ||
    osLower.includes("redhat") ||
    osLower.includes("rhel") ||
    osLower.includes("centos") ||
    osLower.includes("fedora")
  ) {
    return "redhat";
  } else if (
    osLower.includes("darwin") ||
    osLower.includes("macos") ||
    osLower.includes("osx") ||
    osLower.includes("mac")
  ) {
    return "macos";
  } else if (osLower.includes("docker") || osLower.includes("container")) {
    return "docker";
  } else if (osLower.includes("linux")) {
    return "linux";
  } else {
    return "unknown";
  }
}

// Helper function to get a friendly OS family name
export function getOSFamilyName(osIconType: string): string {
  switch (osIconType) {
    case "windows":
      return "Windows";
    case "ubuntu":
      return "Ubuntu";
    case "debian":
      return "Debian";
    case "redhat":
      return "Red Hat / CentOS";
    case "macos":
      return "macOS";
    case "docker":
      return "Container";
    case "linux":
      return "Linux";
    default:
      return "Other";
  }
}

export function OSIconList({
  variants,
  size = 16,
  className,
}: {
  variants: Array<{ os: string }>;
  size?: number;
  className?: string;
}) {
  // Get unique OS icon types - group by icon type rather than exact OS string
  const uniqueOSTypes = new Map<string, { os: string }>();

  variants.forEach((variant) => {
    const iconType = getOSIconType(variant.os);
    if (!uniqueOSTypes.has(iconType)) {
      uniqueOSTypes.set(iconType, variant);
    }
  });

  return (
    <div className={cn("flex gap-1", className)}>
      {Array.from(uniqueOSTypes.values()).map((variant, idx) => (
        <OSIcon key={idx} os={variant.os} size={size} />
      ))}
    </div>
  );
}
